import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Message from "./Message";
import TypingDots from "./TypingDots";
import { selectIsActiveStreaming } from "@redux/Hermes/hermesSlice";
import type { HermesRootState, HydrationToolCall } from "@containers/Hermes/types";

const SHOW_BUTTON_PX = 120;

type MessageListProps = {
  onFollowUp: (text: string, confirmToken?: string) => void;
};

export default function MessageList({ onFollowUp }: MessageListProps) {
  const messages = useSelector((state: HermesRootState) => state.hermes.messages);
  const streamingMessage = useSelector((state: HermesRootState) => state.hermes.streamingMessage);
  const status = useSelector((state: HermesRootState) => state.hermes.status);
  const error = useSelector((state: HermesRootState) => state.hermes.error);
  const activeConversationId = useSelector((state: HermesRootState) => state.hermes.activeConversationId);
  // Only treat the stream as live for THIS view when it belongs to the open
  // conversation; a background stream for another one must stay hidden here.
  const isActiveStreaming = useSelector(selectIsActiveStreaming);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showGoToBottom, setShowGoToBottom] = useState(false);
  const isStreaming = isActiveStreaming;
  const chatInProgress = isActiveStreaming || status === "awaiting_response";

  const scrollToBottom = useCallback((behavior: "auto" | "smooth" = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior });
    setShowGoToBottom(false);
  }, []);

  const updateButtonVisibility = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowGoToBottom(distanceFromBottom > SHOW_BUTTON_PX);
  }, []);

  useEffect(() => {
    scrollToBottom("auto");
  }, [activeConversationId, scrollToBottom]);

  useEffect(() => {
    if (isStreaming) scrollToBottom("smooth");
  }, [isStreaming, scrollToBottom]);

  // Whether the view follows the conversation's end. It stays on until the
  // user scrolls up themselves (wheel / touch / keys), and comes back on when
  // they scroll to the bottom again. A distance check alone isn't enough: a
  // large block arriving at once (a chart, a table) jumps the bottom far away
  // and the view would stop following on its own.
  const pinnedRef = useRef(true);
  const unpinIfScrollingUp = useCallback((deltaY: number) => {
    if (deltaY < 0) pinnedRef.current = false;
  }, []);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < SHOW_BUTTON_PX) pinnedRef.current = true;
    updateButtonVisibility();
  }, [updateButtonVisibility]);
  const touchYRef = useRef<number | null>(null);

  useEffect(() => {
    pinnedRef.current = true;
  }, [activeConversationId]);

  // The list's height changes when an action card replaces the composer (it is
  // taller) or the window resizes; stay at the end if the user was following it.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) bottomRef.current?.scrollIntoView({ behavior: "auto" });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Follow the stream while pinned.
  useEffect(() => {
    if (isStreaming && pinnedRef.current) bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [streamingMessage?.contentSoFar, streamingMessage?.toolCalls, streamingMessage?.structuredBlocks, isStreaming]);

  // The action bars render only once a turn has finished, after the last
  // stream-follow scroll — so finish the follow when the stream ends, or the
  // new bar sits below the fold. Effects run after the DOM is updated, so the
  // bars are already measured here (no animation frame: those pause in a
  // background tab, and a long answer is exactly when the user switches away).
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    const finished = wasStreamingRef.current && !isStreaming;
    wasStreamingRef.current = isStreaming;
    if (finished && pinnedRef.current) scrollToBottom("smooth");
  }, [isStreaming, messages.length, scrollToBottom]);

  useEffect(() => {
    updateButtonVisibility();
  }, [messages.length, streamingMessage?.contentSoFar, streamingMessage?.toolCalls, updateButtonVisibility]);

  return (
    <div className="tw-absolute tw-inset-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onWheel={(e) => unpinIfScrollingUp(e.deltaY)}
        onTouchStart={(e) => {
          touchYRef.current = e.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(e) => {
          const y = e.touches[0]?.clientY;
          // Finger moving down scrolls the content up.
          if (touchYRef.current !== null && y !== undefined) unpinIfScrollingUp(touchYRef.current - y);
          touchYRef.current = y ?? null;
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "Home") pinnedRef.current = false;
        }}
        className="tw-absolute tw-inset-0 tw-overflow-y-auto">
        {/* The item just above the bottom anchor (the last message, or the
            typing/error row) drops its bottom margin, so the end of the
            conversation sits 16px above the composer (Zeplin: Hermes_actionbar). */}
        <div className="tw-max-w-[790px] tw-mx-auto tw-pt-6 tw-pb-1 [&>*:nth-last-child(2)]:tw-mb-0">
          {messages.map((msg) => (
            <Message key={msg.id} message={msg} isStreaming={false} onFollowUp={onFollowUp} disableFollowUp={chatInProgress} />
          ))}

          {isActiveStreaming && streamingMessage && (
            <Message
              key="streaming"
              message={{
                id: 0,
                role: "assistant",
                content: streamingMessage.contentSoFar,
                structured_blocks: streamingMessage.structuredBlocks,
                tool_calls: Object.values(streamingMessage.toolCalls) as unknown as HydrationToolCall[],
                companies: streamingMessage.companies,
                created_at: new Date().toISOString(),
              }}
              isStreaming
              streamingToolCalls={streamingMessage.toolCalls}
              onFollowUp={onFollowUp}
              disableFollowUp={chatInProgress}
            />
          )}

          {status === "awaiting_response" && !isActiveStreaming && (
            <div className="tw-w-full tw-mb-8" role="status" aria-live="polite">
              <TypingDots label="Generating response…" />
            </div>
          )}

          {status === "error" && error && (
            <div role="alert" className="tw-mt-3 tw-rounded-lg tw-border tw-border-error-200 tw-bg-error-50 tw-px-4 tw-py-3 tw-text-sm tw-text-error-600">
              {error}
            </div>
          )}

          <div ref={bottomRef} className="tw-h-1" aria-hidden="true" />
        </div>
      </div>

      {showGoToBottom && (
        <button onClick={() => scrollToBottom("smooth")} aria-label="Scroll to latest" title="Scroll to latest" className="tw-absolute tw-bottom-4 tw-right-4 tw-z-10 tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full tw-bg-white tw-border tw-border-gray-200 tw-shadow-md tw-text-gray-600 hover:tw-text-primary-purple-600 hover:tw-border-primary-purple-300 tw-transition-colors tw-duration-150 tw-cursor-pointer">
          <span className="material-symbols-outlined tw-text-[20px]" aria-hidden="true">
            arrow_downward
          </span>
        </button>
      )}
    </div>
  );
}
